/* =========================================================================
   Telos · Theme JavaScript
   - Cart drawer (open/close, fetch /cart/add.js + /cart.js)
   - Mega-nav (hover/click toggle, focus management)
   - Variant picker (option selection updates /products/:handle.js price/availability)
   - Announcement-bar marquee (clones content for seamless loop)
   - Lazy media (relies on native loading="lazy"; this enhances IntersectionObserver fallback)
   - Simple FAQ accordion (single-open behaviour, schema-friendly)
   - Image gallery with thumbnails on PDP
   - Reading-progress bar on articles
   ========================================================================= */

(function () {
  'use strict';

  const formatMoney = (cents, currency) => {
    const value = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (currency === 'GBP' ? '£' : '£') + value.replace(/\.00$/, '');
  };

  const ready = (fn) => {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  };

  /* ---------------------------------------------------------------------
     Marquee — duplicate content for seamless infinite scroll
     --------------------------------------------------------------------- */
  function initMarquee() {
    document.querySelectorAll('[data-marquee]').forEach((wrap) => {
      const inner = wrap.querySelector('.t-marquee');
      if (!inner) return;
      // duplicate children once so the keyframe -50% loop is seamless
      if (inner.dataset.cloned === 'true') return;
      const clone = inner.innerHTML;
      inner.innerHTML = inner.innerHTML + clone;
      inner.dataset.cloned = 'true';
    });
  }

  /* ---------------------------------------------------------------------
     Mega-nav
     - Hover (pointer:fine) opens panel; leave-with-delay closes
     - Tap toggles open/close on touch and click
     - Escape and outside click close
     - aria-expanded / aria-hidden kept in sync
     - Backdrop dim overlay
     --------------------------------------------------------------------- */
  function initMegaNav() {
    const triggers = document.querySelectorAll('[data-meganav-trigger]');
    const panels = document.querySelectorAll('[data-meganav-panel]');
    const backdrop = document.querySelector('[data-meganav-backdrop]');
    if (!triggers.length) return;

    const fineHover = window.matchMedia('(hover: hover) and (pointer: fine)');
    const HOVER_CLOSE_DELAY = 220;
    let hoverTimer = null;
    let openId = null;

    const setBackdrop = (isOpen) => {
      if (!backdrop) return;
      backdrop.classList.toggle('is-open', isOpen);
    };

    const closeAll = () => {
      triggers.forEach((t) => {
        t.classList.remove('is-active');
        t.setAttribute('aria-expanded', 'false');
      });
      panels.forEach((p) => {
        p.classList.remove('is-open');
        p.setAttribute('aria-hidden', 'true');
      });
      setBackdrop(false);
      openId = null;
    };

    const openPanel = (id) => {
      if (openId === id) return;
      closeAll();
      const trigger = document.querySelector(`[data-meganav-trigger="${id}"]`);
      const panel = document.querySelector(`[data-meganav-panel="${id}"]`);
      if (!trigger || !panel) return;
      trigger.classList.add('is-active');
      trigger.setAttribute('aria-expanded', 'true');
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      setBackdrop(true);
      openId = id;
    };

    const queueClose = () => {
      clearTimeout(hoverTimer);
      hoverTimer = setTimeout(closeAll, HOVER_CLOSE_DELAY);
    };

    const cancelClose = () => clearTimeout(hoverTimer);

    triggers.forEach((trigger) => {
      const id = trigger.getAttribute('data-meganav-trigger');
      const panel = document.querySelector(`[data-meganav-panel="${id}"]`);
      if (!panel) return;

      // Hover (desktop, fine pointer)
      trigger.addEventListener('mouseenter', () => {
        if (!fineHover.matches) return;
        cancelClose();
        openPanel(id);
      });

      trigger.addEventListener('mouseleave', () => {
        if (!fineHover.matches) return;
        queueClose();
      });

      // Keyboard focus opens; closing happens on focusout-of-panel handled below
      trigger.addEventListener('focus', () => {
        cancelClose();
        openPanel(id);
      });

      // Click (touch + mouse) toggles
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        if (openId === id) closeAll();
        else openPanel(id);
      });

      // Keep open when hovering inside panel
      panel.addEventListener('mouseenter', cancelClose);
      panel.addEventListener('mouseleave', () => {
        if (!fineHover.matches) return;
        queueClose();
      });

      // Keyboard nav inside the panel
      panel.addEventListener('keydown', (e) => {
        const focusables = Array.from(
          panel.querySelectorAll('a, button')
        ).filter((el) => !el.hasAttribute('disabled'));
        if (!focusables.length) return;
        const idx = focusables.indexOf(document.activeElement);
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          const next = focusables[Math.min(focusables.length - 1, idx + 1)] || focusables[0];
          next.focus();
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          const prev = focusables[Math.max(0, idx - 1)] || focusables[0];
          prev.focus();
        } else if (e.key === 'Home') {
          e.preventDefault();
          focusables[0].focus();
        } else if (e.key === 'End') {
          e.preventDefault();
          focusables[focusables.length - 1].focus();
        }
      });
    });

    // Escape closes everything
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && openId) {
        const trigger = document.querySelector(`[data-meganav-trigger="${openId}"]`);
        closeAll();
        if (trigger) trigger.focus();
      }
    });

    // Outside click closes
    document.addEventListener('click', (e) => {
      if (!openId) return;
      if (!e.target.closest('[data-meganav-trigger], [data-meganav-panel]')) {
        closeAll();
      }
    });

    // Backdrop click closes
    if (backdrop) {
      backdrop.addEventListener('click', closeAll);
    }

    // When focus leaves the entire mega-nav area (header + panels), close
    document.addEventListener('focusin', (e) => {
      if (!openId) return;
      if (!e.target.closest('[data-meganav-host], [data-meganav-panel]')) {
        closeAll();
      }
    });
  }

  /* ---------------------------------------------------------------------
     Mobile accordion (inside mobile drawer)
     --------------------------------------------------------------------- */
  function initMobileAccordion() {
    const triggers = document.querySelectorAll('[data-mobile-acc-trigger]');
    if (!triggers.length) return;
    triggers.forEach((trigger) => {
      const id = trigger.getAttribute('data-mobile-acc-trigger');
      const panel = document.querySelector(`[data-mobile-acc-panel="${id}"]`);
      if (!panel) return;
      trigger.addEventListener('click', () => {
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        // Optionally close other accordions; we keep them independent for now
        trigger.setAttribute('aria-expanded', String(!isOpen));
        if (isOpen) panel.setAttribute('hidden', '');
        else panel.removeAttribute('hidden');
      });
    });
  }

  /* ---------------------------------------------------------------------
     Mobile drawer
     --------------------------------------------------------------------- */
  function initMobileDrawer() {
    const toggles = document.querySelectorAll('[data-mobile-toggle]');
    const drawer = document.querySelector('[data-mobile-drawer]');
    const close = document.querySelector('[data-mobile-close]');
    if (!drawer) return;

    const open = () => {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      // Move focus into the drawer for keyboard users
      if (close) close.focus();
    };
    const shut = () => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };

    toggles.forEach((t) => t.addEventListener('click', open));
    if (close) close.addEventListener('click', shut);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) shut();
    });
  }

  /* ---------------------------------------------------------------------
     Cart drawer
     --------------------------------------------------------------------- */
  const Cart = {
    drawer: null,
    overlay: null,
    items: null,
    subtotal: null,

    init() {
      this.drawer = document.querySelector('[data-cart-drawer]');
      this.overlay = document.querySelector('[data-cart-overlay]');
      this.items = document.querySelector('[data-cart-items]');
      this.subtotal = document.querySelector('[data-cart-subtotal]');

      document.querySelectorAll('[data-cart-toggle]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
          this.fetch();
        });
      });

      const closers = document.querySelectorAll('[data-cart-close]');
      closers.forEach((c) => c.addEventListener('click', () => this.close()));
      if (this.overlay) this.overlay.addEventListener('click', () => this.close());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });

      // Intercept product form submits to add via AJAX
      document.querySelectorAll('[data-product-form]').forEach((form) => {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.add(form);
        });
      });

      // Item remove / qty change
      if (this.drawer) {
        this.drawer.addEventListener('click', (e) => {
          const remove = e.target.closest('[data-cart-remove]');
          if (remove) {
            e.preventDefault();
            this.update(remove.dataset.cartRemove, 0);
          }
          const inc = e.target.closest('[data-cart-inc]');
          if (inc) {
            const key = inc.dataset.cartInc;
            const qty = parseInt(inc.dataset.qty, 10) || 1;
            this.update(key, qty + 1);
          }
          const dec = e.target.closest('[data-cart-dec]');
          if (dec) {
            const key = dec.dataset.cartDec;
            const qty = parseInt(dec.dataset.qty, 10) || 1;
            if (qty - 1 <= 0) this.update(key, 0);
            else this.update(key, qty - 1);
          }
        });
      }
    },

    open() {
      if (this.drawer) this.drawer.classList.add('is-open');
      if (this.overlay) this.overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    },

    close() {
      if (this.drawer) this.drawer.classList.remove('is-open');
      if (this.overlay) this.overlay.classList.remove('is-open');
      document.body.style.overflow = '';
    },

    add(form) {
      const data = new FormData(form);
      const submit = form.querySelector('[type="submit"]');
      if (submit) submit.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        body: data,
        headers: { 'Accept': 'application/json' }
      })
        .then((r) => r.json())
        .then(() => this.fetch())
        .then(() => this.open())
        .catch((err) => console.error('Cart add failed:', err))
        .finally(() => { if (submit) submit.disabled = false; });
    },

    update(key, qty) {
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      })
        .then((r) => r.json())
        .then(() => this.fetch());
    },

    fetch() {
      return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then((r) => r.json())
        .then((cart) => this.render(cart));
    },

    render(cart) {
      // Update header count
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? '' : 'none';
      });

      if (!this.items) return;

      if (cart.item_count === 0) {
        this.items.innerHTML = '<div class="t-cart-drawer__empty">Your bag is empty.</div>';
      } else {
        this.items.innerHTML = cart.items.map((item) => `
          <div class="t-cart-drawer__item">
            <div class="t-cart-drawer__item-img">
              ${item.image ? `<img src="${item.image}" alt="${item.product_title}" loading="lazy" width="80" height="100" />` : ''}
            </div>
            <div>
              <a href="${item.url}" style="color: inherit; text-decoration: none;">
                <h3 class="t-cart-drawer__item-name">${item.product_title}</h3>
              </a>
              ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="t-cart-drawer__item-variant">${item.variant_title}</div>` : ''}
              <div class="t-cart-drawer__qty">
                <button type="button" data-cart-dec="${item.key}" data-qty="${item.quantity}" aria-label="Decrease quantity">−</button>
                <input type="text" value="${item.quantity}" readonly aria-label="Quantity" />
                <button type="button" data-cart-inc="${item.key}" data-qty="${item.quantity}" aria-label="Increase quantity">+</button>
              </div>
              <button type="button" class="t-cart-drawer__item-remove" data-cart-remove="${item.key}">Remove</button>
            </div>
            <div class="t-cart-drawer__item-price">${formatMoney(item.final_line_price, cart.currency)}</div>
          </div>
        `).join('');
      }

      if (this.subtotal) {
        this.subtotal.textContent = formatMoney(cart.total_price, cart.currency);
      }
    }
  };

  /* ---------------------------------------------------------------------
     Variant picker — updates price/availability via /products/:handle.js
     --------------------------------------------------------------------- */
  const VariantPicker = {
    init() {
      document.querySelectorAll('[data-variant-picker]').forEach((root) => {
        const handle = root.dataset.handle;
        if (!handle) return;

        // Lazy fetch product JSON once
        let productPromise = null;
        const getProduct = () => {
          if (!productPromise) {
            productPromise = fetch(`/products/${handle}.js`, { headers: { 'Accept': 'application/json' } })
              .then((r) => r.json());
          }
          return productPromise;
        };

        root.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-variant-option]');
          if (!btn) return;
          const pos = btn.dataset.position;
          const value = btn.dataset.value;

          // Toggle active state in this option group
          root.querySelectorAll(`[data-variant-option][data-position="${pos}"]`).forEach((el) => el.classList.remove('is-active'));
          btn.classList.add('is-active');

          // Update display
          const display = root.querySelector(`[data-opt-display="${pos}"]`);
          if (display) display.textContent = value;

          // Find matching variant
          const selected = {};
          root.querySelectorAll('[data-variant-option].is-active').forEach((el) => {
            selected[el.dataset.position] = el.dataset.value;
          });

          getProduct().then((product) => {
            const variant = product.variants.find((v) => {
              return (
                (!selected['1'] || v.option1 === selected['1']) &&
                (!selected['2'] || v.option2 === selected['2']) &&
                (!selected['3'] || v.option3 === selected['3'])
              );
            });
            if (!variant) return;

            // Update id input
            const idInput = root.querySelector('[data-variant-id]');
            if (idInput) idInput.value = variant.id;

            // Update price displays
            const price = formatMoney(variant.price, product.currency || 'GBP');
            root.querySelectorAll('[data-price]').forEach((el) => { el.textContent = price; });

            // Update availability
            const submit = root.querySelector('[data-product-submit]');
            if (submit) {
              if (variant.available) {
                submit.disabled = false;
                if (submit.dataset.label) submit.querySelector('[data-submit-text]').textContent = submit.dataset.label;
                else submit.querySelector('[data-submit-text]').textContent = 'Add to bag';
              } else {
                submit.disabled = true;
                submit.querySelector('[data-submit-text]').textContent = 'Sold out';
              }
            }

            // Update URL with variant id (without reload)
            try {
              const url = new URL(window.location);
              url.searchParams.set('variant', variant.id);
              window.history.replaceState({}, '', url);
            } catch (e) { /* no-op */ }
          });
        });
      });
    }
  };

  /* ---------------------------------------------------------------------
     PDP image gallery — thumbnail click swaps main image
     --------------------------------------------------------------------- */
  function initGallery() {
    document.querySelectorAll('[data-gallery]').forEach((gallery) => {
      const main = gallery.querySelector('[data-gallery-main]');
      const thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
      if (!main || !thumbs.length) return;

      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', (e) => {
          e.preventDefault();
          thumbs.forEach((t) => t.classList.remove('is-active'));
          thumb.classList.add('is-active');

          const src = thumb.dataset.fullsrc || thumb.querySelector('img')?.getAttribute('data-full') || thumb.querySelector('img')?.src;
          const alt = thumb.querySelector('img')?.alt || '';
          if (src) {
            const img = main.querySelector('img');
            if (img) {
              // build a parallel srcset for the new image so responsive picking matches src
              const baseSrc = src.replace(/([?&])width=\d+/, '$1width=1200');
              const widths = [600, 900, 1200, 1600];
              const newSrcset = widths.map((w) => baseSrc.replace(/([?&])width=\d+/, '$1width=' + w) + ' ' + w + 'w').join(', ');
              img.setAttribute('srcset', newSrcset);
              img.src = baseSrc;
              img.alt = alt;
            }
          }

          const label = main.querySelector('[data-gallery-label]');
          if (label && thumb.dataset.label) label.textContent = thumb.dataset.label;

          const counter = main.querySelector('[data-gallery-counter]');
          if (counter) {
            const idx = Array.from(thumbs).indexOf(thumb) + 1;
            counter.textContent = String(idx).padStart(2, '0') + ' / ' + String(thumbs.length).padStart(2, '0');
          }
        });
      });
    });
  }

  /* ---------------------------------------------------------------------
     FAQ — single-open accordion behaviour
     --------------------------------------------------------------------- */
  function initFAQ() {
    document.querySelectorAll('[data-faq]').forEach((faq) => {
      faq.addEventListener('toggle', (e) => {
        const target = e.target;
        if (!target.matches('details') || !target.open) return;
        // close siblings
        faq.querySelectorAll('details').forEach((d) => {
          if (d !== target) d.open = false;
        });
      }, true);
    });
  }

  /* ---------------------------------------------------------------------
     Tabs (PDP description / specs / installation)
     --------------------------------------------------------------------- */
  function initTabs() {
    document.querySelectorAll('[data-tabs]').forEach((tabs) => {
      const triggers = tabs.querySelectorAll('[data-tab]');
      const panels = tabs.querySelectorAll('[data-tab-panel]');

      triggers.forEach((trigger) => {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          const target = trigger.dataset.tab;
          triggers.forEach((t) => t.classList.toggle('is-active', t.dataset.tab === target));
          panels.forEach((p) => {
            const isMatch = p.dataset.tabPanel === target;
            p.style.display = isMatch ? '' : 'none';
            p.setAttribute('aria-hidden', String(!isMatch));
          });
        });
      });
    });
  }

  /* ---------------------------------------------------------------------
     Reading progress bar (article pages)
     --------------------------------------------------------------------- */
  function initProgress() {
    const bar = document.querySelector('[data-progress]');
    const target = document.querySelector('[data-progress-target]');
    if (!bar || !target) return;

    const update = () => {
      const rect = target.getBoundingClientRect();
      const total = target.offsetHeight - window.innerHeight;
      const scrolled = Math.max(0, -rect.top);
      const pct = total > 0 ? Math.min(100, Math.max(0, (scrolled / total) * 100)) : 0;
      bar.style.width = pct + '%';
    };

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ---------------------------------------------------------------------
     Newsletter inline confirmation
     --------------------------------------------------------------------- */
  function initNewsletter() {
    document.querySelectorAll('[data-newsletter-form]').forEach((form) => {
      form.addEventListener('submit', () => {
        // Shopify customers/subscribe redirects — set a sessionStorage flag so the next render shows confirmation
        try { sessionStorage.setItem('telos:newsletter:subscribed', '1'); } catch (e) {}
      });
    });

    try {
      if (sessionStorage.getItem('telos:newsletter:subscribed') === '1') {
        document.querySelectorAll('[data-newsletter-success]').forEach((el) => { el.hidden = false; });
        document.querySelectorAll('[data-newsletter-form]').forEach((el) => { el.hidden = true; });
      }
    } catch (e) {}
  }

  /* ---------------------------------------------------------------------
     SEO long-form expand/collapse (collection page)
     --------------------------------------------------------------------- */
  function initExpander() {
    document.querySelectorAll('[data-expander]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const target = document.querySelector(btn.dataset.expander);
        if (!target) return;
        const open = target.getAttribute('aria-expanded') === 'true';
        target.setAttribute('aria-expanded', String(!open));
        target.style.maxHeight = open ? '0' : (target.scrollHeight + 'px');
        btn.textContent = open ? btn.dataset.openLabel || '+ Read the long version' : btn.dataset.closeLabel || '— Read less';
      });
    });
  }

  /* ---------------------------------------------------------------------
     Article TOC — generate from headings in article content
     --------------------------------------------------------------------- */
  function initTOC() {
    const tocList = document.querySelector('[data-toc]');
    const content = document.querySelector('[data-article-content]');
    if (!tocList || !content) return;

    const headings = content.querySelectorAll('h2, h3');
    if (!headings.length) return;

    // Slugify for IDs and ensure uniqueness
    const used = new Set();
    const slugify = (str) => {
      let out = String(str).toLowerCase();
      try { out = out.normalize('NFKD').replace(/[̀-ͯ]/g, ''); } catch (e) {}
      out = out.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64);
      return out || 'section';
    };

    // Replace existing default rail items
    tocList.innerHTML = '';

    headings.forEach((h) => {
      let id = h.id;
      if (!id) {
        let base = slugify(h.textContent || 'section');
        id = base;
        let n = 2;
        while (used.has(id) || document.getElementById(id)) {
          id = base + '-' + n++;
        }
        h.id = id;
      }
      used.add(id);

      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = '#' + id;
      a.textContent = h.textContent.trim();
      if (h.tagName === 'H3') a.classList.add('t-toc-h3');
      li.appendChild(a);
      tocList.appendChild(li);
    });

    // Active-state scroll spy
    const links = tocList.querySelectorAll('a');
    if (!('IntersectionObserver' in window)) return;

    const byId = {};
    links.forEach((a) => { byId[a.getAttribute('href').slice(1)] = a; });

    let activeId = null;
    const setActive = (id) => {
      if (id === activeId) return;
      activeId = id;
      links.forEach((a) => a.classList.remove('is-active'));
      if (byId[id]) byId[id].classList.add('is-active');
    };

    const observer = new IntersectionObserver((entries) => {
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible[0]) setActive(visible[0].target.id);
    }, { rootMargin: '-20% 0px -70% 0px', threshold: 0 });

    headings.forEach((h) => observer.observe(h));
  }

  /* ---------------------------------------------------------------------
     Copy article URL to clipboard
     --------------------------------------------------------------------- */
  function initCopyLink() {
    document.querySelectorAll('[data-copy-link]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const url = window.location.href;
        const original = btn.textContent;
        const done = () => {
          btn.textContent = 'Copied';
          setTimeout(() => { btn.textContent = original; }, 1400);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(done).catch(() => {
            try { window.prompt('Copy this link:', url); } catch (_) {}
          });
        } else {
          try { window.prompt('Copy this link:', url); } catch (_) {}
        }
      });
    });
  }

  /* ---------------------------------------------------------------------
     Lazy media — IntersectionObserver fallback for [data-src]
     --------------------------------------------------------------------- */
  function initLazy() {
    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const el = entry.target;
          if (el.dataset.src) { el.src = el.dataset.src; el.removeAttribute('data-src'); }
          if (el.dataset.srcset) { el.srcset = el.dataset.srcset; el.removeAttribute('data-srcset'); }
          observer.unobserve(el);
        }
      });
    }, { rootMargin: '200px' });

    document.querySelectorAll('img[data-src], img[data-srcset]').forEach((img) => observer.observe(img));
  }

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */
  /* ---------------------------------------------------------------------
     Research page — reading progress bar + active chapter highlight
     --------------------------------------------------------------------- */
  function initResearch() {
    const content = document.querySelector('[data-research-content]');
    const bar = document.querySelector('[data-research-progress]');
    const tocLinks = document.querySelectorAll('[data-research-toc] a');
    if (!content) return;

    function updateProgress() {
      if (!bar) return;
      const rect = content.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      const passed = Math.min(Math.max(-rect.top, 0), Math.max(total, 1));
      const pct = Math.min((passed / Math.max(total, 1)) * 100, 100);
      bar.style.width = pct + '%';
    }

    function updateActiveChapter() {
      if (!tocLinks.length) return;
      const chapters = content.querySelectorAll('[id^="ch-"]');
      let activeId = null;
      const offset = 140;
      chapters.forEach((ch) => {
        const top = ch.getBoundingClientRect().top;
        if (top - offset <= 0) activeId = ch.id;
      });
      tocLinks.forEach((link) => {
        const target = link.getAttribute('href') || '';
        link.classList.toggle('is-active', target === '#' + activeId);
      });
    }

    function loop() {
      updateProgress();
      updateActiveChapter();
    }

    window.addEventListener('scroll', loop, { passive: true });
    window.addEventListener('resize', loop, { passive: true });
    loop();

    // smooth scroll for TOC links
    tocLinks.forEach((link) => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href') || '';
        if (!href.startsWith('#')) return;
        const target = document.querySelector(href);
        if (!target) return;
        e.preventDefault();
        const top = target.getBoundingClientRect().top + window.scrollY - 100;
        window.scrollTo({ top, behavior: 'smooth' });
      });
    });
  }

  ready(() => {
    initMarquee();
    initMegaNav();
    initMobileDrawer();
    initMobileAccordion();
    Cart.init();
    VariantPicker.init();
    initGallery();
    initFAQ();
    initTabs();
    initProgress();
    initNewsletter();
    initExpander();
    initTOC();
    initCopyLink();
    initLazy();
    initResearch();
  });

  // Expose for theme editor / other scripts if needed
  window.Telos = {
    Cart,
    formatMoney
  };
})();
